/**
 * @file setting-row.tsx
 * @module app/modules/setting/components/setting-row
 * @description Row layout for a single setting item: title/description on the
 * left, control (and optional warehouse selector) on the right.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.5
 * @version 2.1.2
 */

import { type ReactNode, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

interface ISettingRowProps {
  title: string;
  description: string;
  control: ReactNode;
  codeSample?: string;
}

/**
 * Row renderer for a single setting item.
 * @param {ISettingRowProps} props Row props.
 * @returns {JSX.Element} Rendered setting row.
 */
const SettingRow = ({ title, description, control, codeSample = '' }: ISettingRowProps) => {
  const [copied, setCopied] = useState(false);
  const sample = codeSample.trim();
  const descriptionHtml = description.replace(/\\n/g, '<br>');

  const copySample = async () => {
    if (!sample) {
      return;
    }
    await navigator.clipboard.writeText(sample);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Row className="setting-row gx-6">
      <Col md={6} className="text-start">
        <h6 className="mb-1 fw-semibold">{title}</h6>
        <div
          className="mb-0 text-muted fs-13 setting-description"
          dangerouslySetInnerHTML={{ __html: descriptionHtml }}
        />
        {sample && (
          <div className="setting-code-sample">
            <button
              type="button"
              className="setting-code-copy"
              aria-label={copied ? 'Copied' : 'Copy default code'}
              title={copied ? 'Copied' : 'Copy'}
              onClick={() => {
                void copySample();
              }}
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path fill="currentColor" d="M6.2 11.2 3.4 8.4l1-1 1.8 1.8 5.4-5.4 1 1z" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path fill="currentColor" d="M5 1.5h6A1.5 1.5 0 0 1 12.5 3v1H13A1.5 1.5 0 0 1 14.5 5.5v8A1.5 1.5 0 0 1 13 15H6a1.5 1.5 0 0 1-1.5-1.5V13H4A1.5 1.5 0 0 1 2.5 11.5v-8A1.5 1.5 0 0 1 4 2h1zm1 1.5H4v8h.5V5.5A1.5 1.5 0 0 1 6 4h5.5V3H6zm1 2v8h6v-8z" />
                </svg>
              )}
            </button>
            <pre className="setting-code-sample-body mb-0">{sample}</pre>
          </div>
        )}
      </Col>
      <Col md={6} className="d-flex justify-content-end align-items-start mt-4 mt-md-0">
        <div className="setting-control-wrap row g-3">{control}</div>
      </Col>
    </Row>
  );
};

export default SettingRow;
